/**
 * sl-xtes.2: Shared AI Key availability check hook
 *
 * Used by ProcessTab (continuous check) and Publish-Gate (on-demand check).
 * DRY: Both consumers call the same backend endpoint.
 */

import { useCallback, useEffect, useState } from 'react';

import { workspaceApiFetch } from '../../cloud';
import { AutomationConfigApiService } from '../services/automation-config-api';

export interface AiKeyCheckResult {
  usesAi: boolean;
  providers: string[];
  missingKeys: string[];
}

/**
 * Reactive AI key check — re-checks when blockId/workflowId/workspaceId change.
 * Used by ProcessTab for continuous monitoring.
 */
export function useAiKeyCheck(
  configApiService: AutomationConfigApiService,
  blockId: string | null | undefined,
  workspaceId: string | null | undefined,
  workflowId: string | null | undefined
): string[] {
  const [missingKeys, setMissingKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!blockId || !workflowId || !workspaceId) {
      setMissingKeys([]);
      return;
    }

    let cancelled = false;
    configApiService
      .checkAiKeys(blockId, workspaceId)
      .then(result => {
        if (!cancelled) {
          setMissingKeys(result.missingKeys);
        }
      })
      .catch(() => {
        if (!cancelled) setMissingKeys([]);
      });

    return () => {
      cancelled = true;
    };
  }, [blockId, workflowId, workspaceId, configApiService]);

  return missingKeys;
}

/**
 * On-demand AI key check — returns a function that checks once when called.
 * Used by Publish-Gate for pre-publish validation.
 * Uses the workflow resource route; no fake config/block identity.
 */
export async function checkAiKeysOnDemand(
  workspaceId: string,
  workflowId: string
): Promise<AiKeyCheckResult> {
  const res = await workspaceApiFetch(
    `/api/workspace/${workspaceId}/workflow/workflows/${workflowId}/ai-key-check`
  );
  if (!res.ok) {
    throw new Error(`AI key check failed: ${res.status}`);
  }
  return (await res.json()) as AiKeyCheckResult;
}

/**
 * Hook that returns a stable callback for on-demand AI key checks.
 * Used by workflow-fullscreen-viewer for publish-gate preflight.
 */
export function useAiKeyCheckOnDemand(
  workspaceId: string | undefined
): (workflowId: string) => Promise<AiKeyCheckResult> {
  return useCallback(
    (workflowId: string): Promise<AiKeyCheckResult> => {
      if (!workspaceId) {
        return Promise.reject(new Error('Workspace required for AI key check'));
      }
      return checkAiKeysOnDemand(workspaceId, workflowId);
    },
    [workspaceId]
  );
}
